/**
 * @file   src\Pages\AddSubscription.tsx
 * @brief  AddSubscription page.
 * @date   JUL, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import { useState, useRef, useEffect, Container, Button, Breadcrumb, Row, Col, useOutletContext, useNavigate, useParams, React } from '../utils/thirdpartyComponents';
import "../styles/subscriptions.scss";
import CWSelect from '../components/CWSelect';
import CWInput from '../components/CWInput';
import CWCheckbox from '../components/CWCheckbox';
import CWInputGroup from '../components/CWInputGroup';
import { pageURLs } from '../utils/pageUrls';
import * as Yup from 'yup'
import { MessageToaster } from '../utils/toastUtil';
import { addSubscriptionSchema } from '../validations/addSubscriptionSchema';
import { AMOUNT_MAX_LENGTH, WASH_DETAIL_MAX_LENGTH, WASH_NAME_MAX_LENGTH, WASH_NUMBER_MAX_LENGTH } from '../utils/constants';
import { AppContextType } from '../interfaces/generalInterface';
import CWMultiSelect from '../components/CWMultiSelect';
import axios from '../config/axiosConfig';
import { CompanyApi, LocationApi, SubscriptionApi } from '../utils/apiUrls';
import { LocationItem, Option, SubscriptionDataItem } from '../interfaces/subscriptionInterface';
import GenerateLink from '../components/GenerateLink';
import CarWashStatus from '../components/CarWashStatus';
import CWTextarea from '../components/CWTextarea';
import SmallLoader from '../components/SmallLoader';
import { userType } from '../utils/enums';
import { CommonMessages } from '../messages/validationMessages';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import { Essentials, Paragraph, Bold, Italic, Heading } from 'ckeditor5';
import ClassicEditor from '../utils/classicEditor';

    interface DiscountData {
  [key: string]: number | string; // allow dynamic keys like discount1...discount10
}
interface ErrorData {
  [key: string]: string;
}

const AddSubscription = (props: any) => {

    const carwash_owner = (props.userType === userType['CARWASH-OWNER']) ? true : false
    // State to handle locations given in dropdown
    const [locations, setLocations] = useState<Option[]>([]);
    const [categoryOptions, setCategoryOptions] = useState<Option[]>([]);
    const [companyDetails, setCompanyDetails] = useState<any>([]);
    const [isFamilyPlan,setIsFamilyPlan]=useState(false);
    const [familyPlanErrors,setFamilyPlanErrors] =  useState<ErrorData>({});
    const [discountData, setDiscountData] = useState<DiscountData>({
        discount1: "",
        discount2: "",
        discount3: "",
        discount4: "",
        discount5: "",
        discount6: "",
        discount7: "",
        discount8: "",
        discount9: "",
        discount10: ""
    });
    const [category, setCategory] = useState<number | ''>('');
    // State to handle subscription types given in dropdown
    const subscriptionTypes = [
        { label: "Single wash", value: "day" },
        { label: "Monthly", value: "month" },
    ];

    // Use a ref to store input elements
    const formRef = useRef<HTMLFormElement>(null);

    // Initialize states for formdata

    const param = useParams();
    let subscription_type = param.type;
    if (subscription_type !== 'day' && subscription_type !== 'month') {
        subscription_type = 'month'
    }

    const [formData, setFormData] = useState<SubscriptionDataItem>({
        location: [],
        subscription: subscription_type,
        washname: '',
        washdetails: '',
        washnumber: '1',
        maxwashperday: '',
        amount: '',
        isTrial: false,
        trialPeriod: '0',
        trialAmount: '',
        isFree: false,
        hasVacuumWash: false,
        numberOfVacuumWashes: '0',
        maxVacuumTime: '0',
        userFamilyDiscount:null,
        isUnlimitedWash:false,
        isPriceHoldEnabled: false,
        isPriceHoldEmail: false,
        hasSelfService:false,
        numberOfSelfServices:'0',
        maxSelfServiceTime:'0',
    })
    // Initialise states for errors
    const [errors, setErrors] = useState({
        location: '',
        subscription: '',
        washname: '',
        washdetails: '',
        washnumber: '',
        maxwashperday: '',
        amount: '',
        trialAmount: '',
        trialPeriod: '',
        hasVacuumWash: '',
        numberOfVacuumWashes: '',
        maxVacuumTime: '',
        numberOfSelfServices:'',
        maxSelfServiceTime:"",
        category: '',
    });

    // State to handle selected location
    const [selectedLocation, setSelectedLocation] = useState<Option[]>([]);
    const [showCategory, setShowCategory] = useState(false);
    const categoryVisibilityRequest = useRef(0);

    // State to company details
    const [company, setCompany] = useState({
        companyName: "",
        address1: "",
        address2: "",
        city: "",
        state: "",
        zipCode: ""
    });

    const [loading, setLoading] = useState(true);

    // Access the context
    const { updateData } = useOutletContext<AppContextType>();

    const navigate = useNavigate()

    const toaster = new MessageToaster();

    useEffect(() => {
        getCompany()
        getCompanyAddress();
        getCategories();
    }, [])


    useEffect(() => {
        // To display heading
        handleUpdate()
    }, [company])

    // Update the data
    const handleUpdate = () => {
        updateData({ heading: carwash_owner ? subscription_type === 'day' ? 'Add Single Wash' : 'Add Subscription' : company.companyName, url: carwash_owner ? `/${pageURLs.manage_subscriptions}?type=${subscription_type}` : `/superadmin/${pageURLs.manage_subscriptions}/${param.id}`, showBackButton: true, showMainMenu: true, activeLink: carwash_owner ? "manage-subscriptions" : "manage-car-wash" });
    };

    const handleDiscountChange = (e:any) => {
    const { name, value } = e.target;
      // Prevent non-numeric input (allow empty and decimals)
    if (value !== "" && !/^\d*\.?\d*$/.test(value)) {
      setFamilyPlanErrors((prev) => ({
        ...prev,
        [name]: "Please enter a valid number",
      }));
      return;
    }
    setDiscountData((prev) => ({
      ...prev,
      [name]: value
    }));

       if (!value.trim()) {
      setFamilyPlanErrors((prev) => ({
        ...prev,
        [name]: "Family plan amount is required",
      }));
    } else if (isNaN(Number(value))) {
      setFamilyPlanErrors((prev) => ({
        ...prev,
        [name]: "Please enter a valid number",
      }));
    } else {
      setFamilyPlanErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

    // Function to fetch company information
    const getCompany = async () => {
        try {
            const param1 = `${param.id}`;
            const { data } = (!carwash_owner) ?
                await axios.get(CompanyApi.GET_COMPANY + param1) :
                await axios.get(CompanyApi.CARWASH_OWNER_COMPANY_DETAILS);

            // To get locations under company
            await getCompanyLocations(data.data.carWashGUID)
            setCompanyDetails(data.data)

        } catch (error: any) {
            console.error('Error fetching company information:', error.message);
            throw new Error(error.response?.data?.message || error.message);
        }
    };


    // To get company details
    const getCompanyAddress = async () => {
        try {
            const id = param.id ? parseInt(param.id) : 0;
            const { data } = (!carwash_owner) ?
                await axios.get(CompanyApi.GET_COMPANY_ADDRESS + id) :
                await axios.get(CompanyApi.CARWASH_OWNER_COMPANY_ADDRESS)
            if (data?.error === 0) {
                setCompany(data.data);
            }
            setLoading(false)
        } catch (error: any) {
            throw new Error(error.response?.data?.message || error.message);
        }
    }

    const getCompanyLocations = async (carwashGuid: string) => {
        try {

            const { data } = (!carwash_owner) ?
                await axios.get(LocationApi.GET_COMPANY_LOCATIONS + carwashGuid)
                : await axios.get(LocationApi.CARWASH_OWNER_GET_COMPANY_LOCATIONS);
            if (data.error === 0) {
                const location_list: LocationItem[] = data?.data

                // Transform the array
                const transformedArray: Option[] = location_list.map(item => ({
                    label: item.locationName,
                    value: item.locationId,
                }));

                setLocations(transformedArray)
            }
            else {
                // show error message
                toaster.toastError(data.message);
            }
        } catch (error: any) {
            throw new Error(error.response?.data?.message || error.message);
        }
    }

    const getCategories = async () => {
        try {
            const { data } = await axios.get(
                !carwash_owner
                    ? LocationApi.ADMIN_GET_CATEGORIES
                    : LocationApi.GET_CATEGORIES
            );
            if (data.error === 0) {
                const categoryList = Array.isArray(data?.data) ? data.data : [];
                const transformedArray: Option[] = categoryList
                    .map((item: any) => ({
                        label: item.washCategoryName ?? '',
                        value: item.washCategoryId ?? 0,
                    }))
                    .filter((item: Option) => item.label && item.value !== undefined && item.value !== null);

                setCategoryOptions(transformedArray);
            }
            else {
                toaster.toastError(data.message);
            }
        } catch (error: any) {
            throw new Error(error.response?.data?.message || error.message);
        }
    }

    const isComboWashLocation = (locationDetails: any) => {
        const comboWash = locationDetails?.comboWash;
            

        return comboWash === 1 || comboWash === true ;
    }

    const resetCategory = () => {
        setCategory('');
        setErrors((prevErrors) => ({
            ...prevErrors,
            category: ''
        }));
    }

    const updateCategoryVisibility = async (selectedOptions: Option[]) => {
        const requestId = categoryVisibilityRequest.current + 1;
        categoryVisibilityRequest.current = requestId;
        const selectedLocations = selectedOptions || [];

        if (!selectedLocations.length) {
            setShowCategory(false);
            resetCategory();
            return;
        }

        try {
            const locationDetails = await Promise.all(
                selectedLocations.map((location) => {
                    const locationDetailsUrl = (!carwash_owner)
                        ? LocationApi.GET_SINGLE_LOCATION
                        : LocationApi.CARWASH_OWNER_LOCATION_DETAILS;

                    return axios.get(locationDetailsUrl + location.value);
                })
            );

            if (requestId !== categoryVisibilityRequest.current) {
                return;
            }

            const hasComboWashLocation = locationDetails.some(({ data }) => isComboWashLocation(data?.data));
            setShowCategory(hasComboWashLocation);

            if (!hasComboWashLocation) {
                resetCategory();
            }
        } catch (error: any) {
            if (requestId !== categoryVisibilityRequest.current) {
                return;
            }

            setShowCategory(false);
            resetCategory();
            // toaster.toastError(error.response?.data?.message || error.message);
        }
    }


    // Triggers whenever options is selected
    const handleSelectChange = (selectedOption: any, name: string) => {

        const value = selectedOption ? selectedOption.value : '';

        // To remove validation when user enters input
        const keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }

        setFormData({
            ...formData,
            [name]: value,
        });

    };

    const handleMultiSelectChange = (selectedOption: Option[], name: string) => {
        setSelectedLocation(selectedOption)
        updateCategoryVisibility(selectedOption);
        // To remove validation when user enters input
        const keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }

        setFormData({
            ...formData,
            [name]: selectedOption,
        });

    }

    const handleCategoryChange = (selectedOption: any) => {
        const value = selectedOption ? selectedOption.value : '';

        setErrors({
            ...errors,
            category: ""
        });
        setCategory(value);
    }

    // Triggers whenever input is entered
    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) => {
        const { name, value } = event.target

        // To remove validation when user enters input
        const keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }

        if (name === 'amount') {
            // Regex to match the input value with up to 2 decimal places
            const regex = /^\d{0,8}(\.\d{0,2})?$/;

            if (regex.test(value)) {
                setFormData({
                    ...formData,
                    [name]: value,
                });
            }
        } else if (name === 'trialAmount') {
            // Regex to match the input value with up to 2 decimal places
            const regex = /^(0|[1-9][0-9]{0,2})?$/
            if (regex.test(value)) {
                const freeChecked = (value == "0") ? true : false
                setFormData({
                    ...formData,
                    [name]: value,
                    ['isFree']: freeChecked,
                });
            }
        } else if (name === 'washnumber') {

            // validate max number of washes per day does not exceed number of washes

            const numberofwashes = parseInt(value);
            const washperDay = parseInt(formData.maxwashperday);

            if (washperDay > numberofwashes && !formData.isUnlimitedWash) {
                setErrors((prev) => ({
                    ...prev,
                    maxwashperday: CommonMessages.wash_per_day_exceed,
                }));
            } else {
                setErrors((prev) => ({
                    ...prev,
                    maxwashperday: '',
                }));
            }

            const regex = /^(0|[1-9][0-9]{0,2})?$/

            if (regex.test(value)) {
                if (value == "0") {
                    setFormData({
                        ...formData,
                        ['isFree']: true
                    });
                }
                setFormData({
                    ...formData,
                    [name]: value,
                });
            }
        } else if (name === 'maxwashperday') {

            // validate max number of washes per day does not exceed number of washes

            const numberofwashes = parseInt(formData.washnumber);
            const washperDay = parseInt(value);


            if (washperDay > numberofwashes && !formData.isUnlimitedWash) {
                setErrors((prev) => ({
                    ...prev,
                    maxwashperday: CommonMessages.wash_per_day_exceed,
                }));
            } else {
                setErrors((prev) => ({
                    ...prev,
                    maxwashperday: '',
                }));
            }

            const regex = /^(0|[1-9][0-9]{0,2})?$/

            if (regex.test(value)) {
                setFormData({
                    ...formData,
                    [name]: value,
                });
            }
        } else {
            setFormData({
                ...formData,
                [name]: value,
            });
        }
    }

    const validateFamilyPlan = (): boolean => {
    const newErrors: any = {};

    Object.entries(discountData).forEach(([key, value]) => {
      if (!value) {
        newErrors[key as keyof DiscountData] = "This field is required";
      }else if (isNaN(Number(value)))  {
        newErrors[key as keyof DiscountData] = "Please enter a valid number";
      }
   
    });

    setFamilyPlanErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


    // Triggers on submit
    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {

        event.preventDefault()
        try {
            if (formData.subscription === "day") {
                // In case of single type subscription washnumber is defaulted to 1
                formData.washnumber = "1"
            }
            if (showCategory && !category) {
                setErrors({
                    ...errors,
                    category: "Select Category."
                });
                return false;
            }
            console.log(discountData)
            formData.userFamilyDiscount = discountData
            // Validate form data
            const isFamilyplanEmpty = Object.values(discountData).every(
                (value) => value === "" || value === null
                );
                if(isFamilyPlan &&   !validateFamilyPlan()) 
                {
                    return
                }

            await addSubscriptionSchema.validate(formData, { abortEarly: false });
            if (formData.isTrial === true && (formData.trialPeriod == "" || formData.trialPeriod == "0")) {
                setErrors({
                    ...errors,
                    ["trialPeriod"]: "Select trial period duration."
                });
                return false;
            }
            if (formData.isTrial === true && formData.isFree === false && formData.trialAmount == "") {
                setErrors({
                    ...errors,
                    ["trialAmount"]: "Enter trial amount."
                });
                return false;
            }

            if (formData.isTrial === true && formData.isFree === false && Number(formData.trialAmount) >= Number(formData.amount)) {
                setErrors({
                    ...errors,
                    ["trialAmount"]: "The trial period amount should be less than the subscription amount."
                });
                return false;
            }

            const locationIdArray = formData.location && formData.location.map(loc => loc.value)

            const newformData = (!carwash_owner) ? {
                carWashId: param.id ? parseInt(param.id) : 0,
                locationIds: locationIdArray,
                subscriptionType: formData.subscription,
                washName: formData.washname,
                subscriptionDetails: formData.washdetails,
                numberOfWashes: parseInt(formData.washnumber),
                dailyWashLimit: parseInt(formData.maxwashperday),
                price: parseFloat(formData.amount),
                isTrial: (formData.isTrial) ? 1 : 0,
                trialPeriod: (formData.isTrial && formData.trialPeriod) ? parseInt(formData.trialPeriod) : 0,
                trialAmount: (formData.trialAmount && formData.isTrial && formData.isFree === false) ? parseFloat(formData.trialAmount) : 0,
                bayType:0,
                washCategoryId: showCategory ? Number(category) : null,
                hasVacuumWash: formData.hasVacuumWash ?? 0,
                numberOfVacuumWashes: Number(formData.numberOfVacuumWashes ?? 0),
                maxVacuumTime: Number(formData.maxVacuumTime ?? 0),
                userFamilyDiscount:isFamilyplanEmpty ? null : discountData,
                isUnlimitedWash:formData.isUnlimitedWash ,
                isPriceHoldEnabled: formData.isPriceHoldEnabled,
                isPriceHoldEmail: formData.isPriceHoldEnabled && formData.isPriceHoldEmail ? true : false,
                hasSelfService:formData.hasSelfService,
                numberOfSelfServices:Number(formData.numberOfSelfServices ?? 0),
                maxSelfServiceTime:Number(formData.maxSelfServiceTime ?? 0)

            } : {
                locationIds: locationIdArray,
                subscriptionType: formData.subscription,
                washName: formData.washname,
                subscriptionDetails: formData.washdetails,
                numberOfWashes: parseInt(formData.washnumber),
                dailyWashLimit: parseInt(formData.maxwashperday),
                price: parseFloat(formData.amount),
                isTrial: (formData.isTrial) ? 1 : 0,
                trialPeriod: (formData.isTrial && formData.trialPeriod) ? parseInt(formData.trialPeriod) : 0,
                trialAmount: (formData.trialAmount && formData.isTrial && formData.isFree === false) ? parseFloat(formData.trialAmount) : 0,
                bayType:0,
                washCategoryId: showCategory ? Number(category) : null,
                hasVacuumWash: formData.hasVacuumWash ,
                numberOfVacuumWashes: Number(formData.numberOfVacuumWashes ?? 0),
                maxVacuumTime: Number(formData.maxVacuumTime ?? 0),
                userFamilyDiscount:isFamilyplanEmpty ? null : discountData,
                isUnlimitedWash:formData.isUnlimitedWash,
                isPriceHoldEnabled: formData.isPriceHoldEnabled,
                isPriceHoldEmail: formData.isPriceHoldEnabled && formData.isPriceHoldEmail ? true : false,
                hasSelfService:formData.hasSelfService,
                numberOfSelfServices:Number(formData.numberOfSelfServices ?? 0),
                maxSelfServiceTime:Number(formData.maxSelfServiceTime ?? 0)


            }

            try {
                setLoading(true)
                const { data } = (!carwash_owner) ?
                    await axios.post(SubscriptionApi.ADD_SUBSCRIPTION, newformData)
                    : await axios.post(SubscriptionApi.CARWASH_OWNER_ADD_SUBSCRIPTION, newformData);
                setLoading(false)

                if (data?.error === 0) {
                    //Clear errors
                    setErrors({
                        location: '',
                        subscription: '',
                        washname: '',
                        washdetails: '',
                        washnumber: '',
                        maxwashperday: '',
                        amount: '',
                        trialAmount: '',
                        trialPeriod: '',
                        hasVacuumWash: '',
                        numberOfVacuumWashes: '',
                        maxVacuumTime: '',
                        numberOfSelfServices:"",
                        maxSelfServiceTime:"",
                        category: '',
                        
    
                    });
                    const subscriptionId = data.data.subscriptionId[0]
                    if (!carwash_owner) {
                        navigate(`/superadmin/${pageURLs.add_relaynumber}/${param.id}/${subscriptionId}`)
                    } else {
                        navigate(`/${pageURLs.add_relaynumber}/${subscriptionId}`, {state: { type: subscription_type }})
                    }
                }
                else {
                    // show error message
                    toaster.toastError(data?.message);
                }
            } catch (error: any) {
                throw new Error(error?.response?.data?.message || error?.message);
            }


        } catch (error) {
            const newErrors: any = {};

            // If validation fails on submit, then errors are shown
            if (error instanceof Yup.ValidationError) {
                error.inner.forEach((err) => {
                    newErrors[err.path as keyof typeof formData] = err.message;
                });
                console.log(newErrors)
                setErrors(newErrors);
            }

            // Focus on the first invalid field
            if (formRef.current) {
                const firstInvalidField = Object.keys(newErrors)[0];
                const invalidElement = formRef.current.querySelector<HTMLInputElement>(`[name="${firstInvalidField}"]`);
                if (invalidElement) {
                    invalidElement.focus();
                }
            }
        }

    }

    // Navigate accordung to url
    const itemClick = async (url: string) => {
        navigate(url);
    }

    const options = [
        { label: 'Select', value: '0' },
        { label: 'One Month', value: '1' },
        { label: 'Two Months', value: '2' },
        { label: 'Three Months', value: '3' },
        { label: 'Four Months', value: '4' },
        { label: 'Five Months', value: '5' },
        { label: 'Six Months', value: '6' },
        { label: 'Seven Months', value: '7' },
        { label: 'Eight Months', value: '8' },
        { label: 'Nine Months', value: '9' },
        { label: 'Ten Months', value: '10' },
        { label: 'Eleven Months', value: '11' },
        { label: 'Twelve Months', value: '12' },
    ];


     const setFreeVacuumData = async (e: any) => {
        if (e.target.name === 'hasVacuumWash') {
            if (e.target.checked === true) {
                if(formData.subscription === 'day')
                {
                    setFormData({
                    ...formData,
                    'numberOfVacuumWashes':'1',
                    [e.target.name]: e.target.checked,
                });
                }
                else{
                    setFormData({
                    ...formData,
                    [e.target.name]: e.target.checked,
                });
                }
                
            } else {
                setFormData({
                    ...formData,
                    [e.target.name]: e.target.checked,
                    ['hasVacuumWash']: false,
                    ['numberOfVacuumWashes']: '0',
                    ['maxVacuumTime']: '0',
                });
            }
        } 
    }


const setFreeSelfServeData = async (e: any) => {
        if (e.target.name === 'hasSelfService') {
            if (e.target.checked === true) {
                if(formData.subscription === 'day')
                {
                    setFormData({
                    ...formData,
                    'numberOfSelfServices':'1',
                    [e.target.name]: e.target.checked,
                });
                }
                else{
                    setFormData({
                    ...formData,
                    [e.target.name]: e.target.checked,
                });
                }
                
            } else {
                setFormData({
                    ...formData,
                    [e.target.name]: e.target.checked,
                    ['hasSelfService']: false,
                    ['numberOfSelfServices']: '0',
                    ['maxVacuumTime']: '0',
                });
            }
        } 
    }

const setUnLimitedData = async (e: any) => {
    if (e.target.checked === true) {
         setFormData({
            ...formData,
            [e.target.name]: e.target.checked,
            ['washnumber']:'0'
        });
     }
     else{
         setFormData({
            ...formData,
            [e.target.name]: e.target.checked,
        });
     }
}
    const setTrailPeriodData = async (e: any) => {
        if (e.target.name === 'isTrial') {
            if (e.target.checked === true) {
                setFormData({
                    ...formData,
                    [e.target.name]: e.target.checked,
                });
            } else {
                setFormData({
                    ...formData,
                    [e.target.name]: e.target.checked,
                    ['trialPeriod']: '0',
                    ['isFree']: false,
                    ['trialAmount']: '',
                });
            }
        } else if (e.target.name === 'isFree') {
            setErrors({
                ...errors,
                ["trialAmount"]: ""
            })
            if (e.target.checked === true) {
                setFormData({
                    ...formData,
                    [e.target.name]: e.target.checked,
                    ['trialAmount']: '0',
                });
            } else {
                setFormData({
                    ...formData,
                    [e.target.name]: e.target.checked,
                    ['trialAmount']: '0',
                });
            }
        }
    }

    return (

        <div className='manage-carwash-container manage-subscription-container'>
            <Container>
                <Breadcrumb>
                    {carwash_owner ? null :
                        <>
                            <Breadcrumb.Item onClick={() => { itemClick(`/superadmin/${pageURLs.manage_carwash}`) }}>Manage Car Washes</Breadcrumb.Item>
                            <Breadcrumb.Item onClick={() => { itemClick(`/superadmin/${pageURLs.carwash_locations}/${param.id}`) }}  >{company.companyName}</Breadcrumb.Item>
                        </>
                    }

                    <Breadcrumb.Item
                        onClick={() => {
                            const url = carwash_owner
                                ? `/${pageURLs.manage_subscriptions}?type=${subscription_type}`
                                : `/superadmin/${pageURLs.manage_subscriptions}/${param.id}`;
                            itemClick(url);
                        }}
                    >
                        {subscription_type === 'day' ? 'Manage Single Wash' : 'Manage Subscriptions'}
                    </Breadcrumb.Item>

                    <Breadcrumb.Item active>{subscription_type === 'day' ? 'Add Single Wash' : 'Add Subscription'}</Breadcrumb.Item>
                </Breadcrumb>
                {carwash_owner ? null :
                    <div className="manage-activity-wrap d-flex justify-content-between align-items-center">
                        {companyDetails && <GenerateLink id={param.id} companyDetails={companyDetails} carwash_owner={carwash_owner} />}
                        <div className="activity-status d-flex">


                            <CarWashStatus carWashId={param.id} status={(companyDetails?.IsCompanyActive == 1) ? true : false} companyName={company.companyName} />
                            {/* <div className="stat-group d-flex align-items-center">

                                <p>Subscription Status</p>
                                <Badge className='active'>Active</Badge>
                            </div> */}

                        </div>
                    </div>}

                <div className='cw-form-outer-wrap'>
                    <form onSubmit={(e) => handleSubmit(e)} ref={formRef}>
                        <div className="cw-form-container">
                            <div className="form-inner mx-auto">

                                <div>
                                    <CWMultiSelect
                                        id="location"
                                        name="location"
                                        options={locations}
                                        label="Select Location"
                                        dropdownGap={0}
                                        dropdownPosition="auto"
                                        isMandatory
                                        placeholder="Select Location"
                                        errorMessage={errors.location}
                                        onChange={(selectedOptions) => handleMultiSelectChange(selectedOptions, "location")}
                                        hasSelectAll={true}
                                        value={selectedLocation}
                                        labelAllSelected="All Locations Selected."
                                    />
                                </div>
                                {subscription_type != "day" && <CWSelect
                                    id="subscription"
                                    name="subscription"
                                    options={subscriptionTypes}
                                    label="Select Subscription Type"
                                    isMandatory
                                    dropdownGap={0}
                                    dropdownPosition="auto"
                                    placeholder="Select Subscription Type"
                                    multi={false}
                                    searchable={false}
                                    errorMessage={errors.subscription}
                                    values={subscriptionTypes.filter(option => option.value === formData.subscription)}
                                    onChange={(selectedOptions) => handleSelectChange(selectedOptions[0], 'subscription')}

                                />}
                                {showCategory && <Row>
                                    <Col md={12}>
                                        <CWSelect
                                            id="category"
                                            name="category"
                                            options={categoryOptions}
                                            label="Category"
                                            isMandatory
                                            dropdownGap={0}
                                            dropdownPosition="auto"
                                            placeholder="Select Category"
                                            multi={false}
                                            searchable={false}
                                            errorMessage={errors.category}
                                            values={categoryOptions.filter(option => option.value === category)}
                                            onChange={(selectedOptions) => handleCategoryChange(selectedOptions[0])}
                                        />
                                    </Col>
                                </Row>}
                                <Row>
                                    <Col md={6}>
                                        <CWInput
                                            id="washname"
                                            name="washname"
                                            type="text"
                                            label="Wash Name"
                                            maxLength={WASH_NAME_MAX_LENGTH}
                                            isMandatory
                                            value={formData.washname}
                                            errorMessage={errors.washname}
                                            onChange={handleInputChange}
                                        />
                                    </Col>
                                    <Col md={6}>
                                    <div className="form-group cw-input">
                                    <div className="cust-label-group">
                                        <label htmlFor="label" >Wash details
                                            <span className="mandatory">*</span>
                                        </label>
                                        
                                    <CKEditor
                                                                            editor={ClassicEditor as any}
                                                                            config={{
                                                                                licenseKey: 'GPL',
                                                                                plugins: [Essentials, Paragraph, Bold, Italic, Heading],
                                                                                toolbar: [
                                                                                    'heading', '|', 'bold', 'italic', 'underline', 'strikethrough', '|',
                                                                                    'link', 'bulletedList', 'numberedList', 'blockQuote', '|',
                                                                                    'undo', 'redo', '|', 'alignment', 'fontSize', 'fontColor',
                                                                                    'fontBackgroundColor', 'highlight', 'codeBlock',
                                                                                    'insertTable', 'imageUpload', 'mediaEmbed'
                                                                                ],
                                                                                heading: {
                                                                                    options: [
                                                                                        { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
                                                                                        { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
                                                                                        { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
                                                                                        { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
                                                                                        { model: 'heading4', view: 'h4', title: 'Heading 4', class: 'ck-heading_heading4' }
                                                                                    ]
                                                                                },
                                                                                image: {
                                                                                    toolbar: [
                                                                                        'imageTextAlternative', 'toggleImageCaption',
                                                                                        'imageStyle:full', 'imageStyle:side'
                                                                                    ]
                                                                                },
                                                                                table: {
                                                                                    contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells']
                                                                                },
                                                                            }}
                                                                            // onReady={(editor) => {
                                                                            //     if (formData.EmailTemplateText) {
                                                                            //         editor.setData(formData.EmailTemplateText);
                                                                            //     }
                                    
                                                                            // }}
                                                                            onChange={(event, editor) => {
                                                                                const data = editor.getData();
                                                                                setFormData((prev) => ({
                                                                                    ...prev,
                                                                                    washdetails: data,
                                                                                }));
                                                                                
                                                                                setErrors({
                                                                                ...errors,
                                                                                ['washdetails']: ""
                                                                            })
                                    
                                                                            }}
                                                                    
                                                                        />
                                                                              {/* {errorMessage && errorMessage !== '' && <span className="error-msg">{errorMessage}</span>} */}

                                                                        <span className='error-msg'>{errors.washdetails}</span>
                                        
                                        </div>
                                        </div>
                                    </Col>
                                </Row>
                                <Row>
                                    {!formData.isUnlimitedWash?<Col md={6}>
                                        <div className="custom-note position-relative">
                                            <CWInput
                                                id="washnumber"
                                                name="washnumber"
                                                type="number"
                                                label="Number of Washes"
                                                maxLength={WASH_NUMBER_MAX_LENGTH}
                                                isMandatory
                                                value={formData.subscription === "day" ? 1 : formData.washnumber}
                                                errorMessage={errors.washnumber}
                                                onChange={handleInputChange}
                                                readOnly={formData.subscription === "day" }
                                            />
                                            <span className="note"><i>*Single Wash value will be set to 1 by default</i></span>
                                        </div>

                                    </Col> :""}

                                    {formData.subscription === "month" ?
                                    <>
                                    <Col md={3}>
                                    <CWCheckbox
                                        id="isUnlimitedWash"
                                        name="isUnlimitedWash"
                                        label="Enable Unlimited Wash"
                                        checked={formData.isUnlimitedWash}
                                        onChange={(e) => { setUnLimitedData(e); }}
                                    />
                                    </Col>
                                    <Col md={3}>
                                    <CWCheckbox
                                        id="isPriceHoldEnabled"
                                        name="isPriceHoldEnabled"
                                        label="60-Day Price Hold After Update"
                                        checked={formData.isPriceHoldEnabled}
                                        onChange={(e) => {
                                            setFormData({
                                                ...formData,
                                                [e.target.name]: e.target.checked,
                                                isPriceHoldEmail: e.target.checked ? formData.isPriceHoldEmail : false,
                                            });
                                        }}
                                    />
                                    {formData.isPriceHoldEnabled ? (
                                    <CWCheckbox
                                        id="isPriceHoldEmail"
                                        name="isPriceHoldEmail"
                                        label="Notify customers by email about subscription price updates"
                                        helptext="Checked: Send an email notification to customers. Unchecked: No email notification will be sent."
                                        checked={formData.isPriceHoldEmail}
                                        onChange={(e) => {
                                            setFormData({
                                                ...formData,
                                                [e.target.name]: e.target.checked,
                                            });
                                        }}
                                    />
                                    ) : null}
                                    </Col>
                                    <Col md={6}>
                                        <CWInput
                                            id="maxwashperday"
                                            name="maxwashperday"
                                            type="number"
                                            label="Maximum Number of Washes per day"
                                            maxLength={WASH_NUMBER_MAX_LENGTH}
                                            isMandatory
                                            value={formData.maxwashperday}
                                            errorMessage={errors.maxwashperday}
                                            onChange={handleInputChange}
                                        />
                                    </Col>
                                    </>
                                         : null}

                                    <Col md={6}>
                                        <CWInputGroup
                                            id="amount"
                                            name="amount"
                                            type="number"
                                            step='0.01'
                                            label="Amount"
                                            labelPrefix="$"
                                            isMandatory
                                            maxLength={AMOUNT_MAX_LENGTH}
                                            value={formData.amount}
                                            errorMessage={errors.amount}
                                            onChange={handleInputChange}
                                        />
                                        
                                    </Col>
                                </Row>
                                {formData.subscription === "month" ?<CWCheckbox
                                    id="Isfamilyplan"
                                    name="Isfamilyplan"
                                    label="Family Plan"
                                    checked={isFamilyPlan}
                                    onChange={(e) => { setIsFamilyPlan(e.target.checked); }}
                                />:""}
                                 {
                                 isFamilyPlan ?
                                 <Row>
        {[...Array(10)].map((_, index) => (
          <Col md={4} key={index}>
            <CWInputGroup
              id={`discount${index + 1}`}
              name={`discount${index + 1}`}
              type="number"
              step='0.01'
              label={`Amount to be paid for Family Plan (Sub User ${index + 1}${index === 0 ? ', not account owner' : ''})`}
              labelPrefix="$"
              maxLength={AMOUNT_MAX_LENGTH}
              value={discountData[`discount${index + 1}`] || ""}
              errorMessage={familyPlanErrors[`discount${index + 1}`]}
              onChange={handleDiscountChange}
            />
          </Col>
        ))}
      </Row>   :""} 
                                    
                                {formData.subscription === 'month' ? <div className="trial-sec mt-4">
                                    <CWCheckbox
                                        id="isTrial"
                                        name="isTrial"
                                        label="Offer Trial Period"
                                        checked={formData.isTrial}
                                        onChange={(e) => { setTrailPeriodData(e); }}
                                    />
                                    {formData.isTrial ? <Row>
                                        <Col md={6}>
                                            <CWSelect
                                                id="duration"
                                                name="trialPeriod"
                                                options={options}
                                                label="Duration"
                                                dropdownGap={0}
                                                dropdownPosition="auto"
                                                placeholder="Select"
                                                multi={false}
                                                isMandatory
                                                className="no-input"
                                                errorMessage={errors.trialPeriod}
                                                values={options.filter(option => option.value === formData.trialPeriod)}
                                                onChange={(selectedOptions) => handleSelectChange(selectedOptions[0], 'trialPeriod')}
                                            />
                                        </Col>
                                        <Col md={6}>
                                            <div className="trial-period">
                                                <p>Amount in trial period</p>
                                                <div className="d-flex align-items-center field-group">
                                                    <CWCheckbox
                                                        id="free"
                                                        name="isFree"
                                                        label="Free"
                                                        checked={formData.isFree}
                                                        onChange={(e) => { setTrailPeriodData(e); }}

                                                    />
                                                    <CWInputGroup
                                                        id="traialamount"
                                                        name="trialAmount"
                                                        type="text"
                                                        labelPrefix="$"
                                                        maxLength={AMOUNT_MAX_LENGTH}
                                                        value={formData.trialAmount}
                                                        errorMessage={errors.trialAmount}
                                                        onChange={handleInputChange}
                                                    />
                                                </div>
                                            </div>
                                        </Col>
                                    </Row> : ''}
                                </div> : ''}
{/* free vacuum wash */}
                                <div className="trial-sec mt-4">
                                    <CWCheckbox
                                        id="hasVacuumWash"
                                        name="hasVacuumWash"
                                        label="Offer Free Vacuum / Mat Washer"
                                        checked={formData.hasVacuumWash}
                                        onChange={(e) => { setFreeVacuumData(e); }}
                                    />
                                    {formData.hasVacuumWash ? <Row>
                                       {formData.subscription === 'month' ?  <Col md={6}>
                                            <CWInput
                                                id="numberOfVacuumWashes"
                                                name="numberOfVacuumWashes"
                                                label="Number of Times per Month"
                                                 maxLength={WASH_NUMBER_MAX_LENGTH}
                                                isMandatory
                                                value={formData.numberOfVacuumWashes}
                                                errorMessage={errors.numberOfVacuumWashes}
                                                onChange={handleInputChange}
                                            />
                                        </Col>:""}
                                        <Col md={6}>
                                                <CWInput
                                                id="maxVacuumTime"
                                                name="maxVacuumTime"
                                                label="Maximum Time per Use (in minutes)"
                                                 maxLength={WASH_NUMBER_MAX_LENGTH}
                                                isMandatory
                                                value={formData.maxVacuumTime}
                                                errorMessage={errors.maxVacuumTime}
                                                onChange={handleInputChange}
                                            />
                                        </Col>
                                    </Row> : ''}
                                </div> 
                                        
                                {/* free self serve wash */}
                                 {formData.subscription === "month" ?
                                <div className="trial-sec mt-4">
                                    <CWCheckbox
                                        id="hasSelfService"
                                        name="hasSelfService"
                                        label="Offer Self Serve"
                                        checked={formData.hasSelfService}
                                        onChange={(e) => { setFreeSelfServeData(e); }}
                                    />
                                    {formData.hasSelfService ? <Row>
                                        <Col md={6}>
                                            <CWInput
                                                id="numberOfSelfServices"
                                                name="numberOfSelfServices"
                                                label="Number of Times per Month"
                                                 maxLength={WASH_NUMBER_MAX_LENGTH}
                                                isMandatory
                                                value={formData.numberOfSelfServices}
                                                errorMessage={errors.numberOfSelfServices}
                                                onChange={handleInputChange}
                                            />
                                        </Col>
                                        <Col md={6}>
                                                <CWInput
                                                id="maxSelfServiceTime"
                                                name="maxSelfServiceTime"
                                                label="Maximum Time per Use (in minutes)"
                                                 maxLength={WASH_NUMBER_MAX_LENGTH}
                                                isMandatory
                                                value={formData.maxSelfServiceTime}
                                                errorMessage={errors.maxSelfServiceTime}
                                                onChange={handleInputChange}
                                            />
                                        </Col>
                                    </Row> : ''}
                                </div> :""}



                            </div>
                        </div>
                        <div className="btn-sec d-flex justify-content-end">
                            <Button title="Cancel" variant="primary" className="secondary-btn" onClick={() => {
                                const url = carwash_owner
                                    ? `/${pageURLs.manage_subscriptions}`
                                    : `/superadmin/${pageURLs.manage_subscriptions}/${param.id}`;
                                navigate(url);
                            }}>Cancel
                            </Button>
                            <Button title="Next" variant="primary" className="primary-btn" type="submit">Next</Button>
                        </div>
                    </form>
                </div>
            </Container>
            {loading && <SmallLoader />}

        </div >
    )
}


export default AddSubscription;
